"use client";

import { useState } from 'react';
import Link from 'next/link';
import { FiCheckCircle } from 'react-icons/fi';

import FreeStartAction from './FreeStartAction';
import PremiumSignupActions from './PremiumSignupActions';
import { localePath } from '@/lib/siteI18n';
import { usePremiumOfferImpression } from '../../lib/premiumOfferImpression';

const formatPrice = (value, currency) => {
  const num = Number(value);
  const display = Number.isFinite(num)
    ? num.toFixed(2).replace(/\.00$/, '')
    : value;
  return currency === 'USD' ? `$${display}` : `${display} ${currency}`;
};

export default function PricingSection({ home, activeLocale, pricing }) {
  const [billingInterval, setBillingInterval] = useState('monthly');
  const offerRef = usePremiumOfferImpression({ ctaLocation: 'pricing', billingInterval, locale: activeLocale });
  const {
    freePlanPrice,
    monthlyProPrice,
    annualProMonthlyPrice,
    annualProTotalPrice,
    annualDiscount,
    enterprisePrice,
    currency,
  } = pricing;
  const isAnnual = billingInterval === 'annual';
  const annualPercent = Math.round(Number(annualDiscount || 0) * 100);
  const selectedProMonthlyPrice = isAnnual ? annualProMonthlyPrice : monthlyProPrice;
  const annualTotal = formatPrice(annualProTotalPrice, currency);
  const priceDetail = isAnnual
    ? home.billingAnnualDetail.replace('${annualTotal}', annualTotal)
    : home.billingMonthlyDetail;

  return (
    <section id="pricing" className="pricing-section">
      <div className="container">
        <h2 className="section-title">{home.pricingTitle}</h2>
        <p className="section-subtitle">{home.pricingSubtitle}</p>

        <div className="pricing-billing-toggle" role="group" aria-label={home.billingToggleLabel}>
          <button
            type="button"
            className={`pricing-toggle-option ${!isAnnual ? 'active' : ''}`}
            aria-pressed={!isAnnual}
            onClick={() => setBillingInterval('monthly')}
          >
            {home.billingMonthly}
          </button>
          <button
            type="button"
            className={`pricing-toggle-option ${isAnnual ? 'active' : ''}`}
            aria-pressed={isAnnual}
            onClick={() => setBillingInterval('annual')}
          >
            <span>{home.billingAnnual}</span>
            <span className="pricing-save-pill">
              {home.billingSave.replace('${percent}', annualPercent)}
            </span>
          </button>
        </div>

        <div className="pricing-plans">
          <div className="pricing-plan">
            <div className="plan-badge">{home.freeForever}</div>
            <h3>{home.freePlan}</h3>
            <p className="price">{formatPrice(freePlanPrice, currency)}</p>
            <p className="price-period">{home.noCard}</p>
            <ul className="plan-features">
              <li><FiCheckCircle /> {home.freeFeature1}</li>
              <li><FiCheckCircle /> {home.freeFeature2}</li>
              <li><FiCheckCircle /> {home.freeFeature3}</li>
              <li><FiCheckCircle /> {home.freeFeature4}</li>
            </ul>
            <div className="pricing-signup-container">
              <FreeStartAction locale={activeLocale} location="pricing_free" />
            </div>
          </div>

          <div ref={offerRef} className="pricing-plan featured">
            <div className="plan-badge">{home.mostPopular}</div>
            <h3>{home.proPlan}</h3>
            <div className="price-row" aria-live="polite">
              <span className="price">{formatPrice(selectedProMonthlyPrice, currency)}</span>
              <span className="price-cycle">{home.billingPerMonth}</span>
            </div>
            <p className="price-period price-period--billing">{priceDetail}</p>
            <ul className="plan-features">
              <li><FiCheckCircle /> {home.proFeature1}</li>
              <li><FiCheckCircle /> {home.proFeature7}</li>
              <li><FiCheckCircle /> {home.proFeature2}</li>
              <li><FiCheckCircle /> {home.proFeature3}</li>
              <li><FiCheckCircle /> {home.proFeature4}</li>
              <li><FiCheckCircle /> {home.proFeature6}</li>
              <li><FiCheckCircle /> {home.proFeature5}</li>
            </ul>
            <PremiumSignupActions locale={activeLocale} billingInterval={billingInterval} ctaLocation="pricing" />
            <p className="plan-note">{home.trialNote}</p>
          </div>

          <div className="pricing-plan">
            <div className="plan-badge">{home.forTeams}</div>
            <h3>{home.enterprise}</h3>
            <p className="price">{enterprisePrice}</p>
            <p className="price-period">{home.contactPricing}</p>
            <ul className="plan-features">
              <li><FiCheckCircle /> {home.enterpriseFeature1}</li>
              <li><FiCheckCircle /> {home.enterpriseFeature2}</li>
              <li><FiCheckCircle /> {home.enterpriseFeature3}</li>
              <li><FiCheckCircle /> {home.enterpriseFeature4}</li>
              <li><FiCheckCircle /> {home.enterpriseFeature5}</li>
            </ul>
            <Link
              href={localePath('/contact', activeLocale)}
              className="btn secondary-btn"
              data-analytics-event="pricing_contact_sales_click"
              data-analytics-cta-id="pricing_contact_sales"
              data-analytics-cta-location="pricing"
              data-analytics-plan-intent="enterprise"
              data-analytics-destination="/contact"
            >
              {home.contactSales}
            </Link>
          </div>
        </div>

        <div className="pricing-guarantee">
          <p>{home.guarantee}</p>
        </div>
      </div>
    </section>
  );
}
